import React, { useState, useRef, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { Bot, X, Send, Sparkles, Loader2, AlertTriangle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SUGGESTIONS = [
  "What sites are under highest pressure this week?",
  "Summarize sentiment trend for Adani Green Energy",
  "Which state has the most critical signals?",
  "Top activists mentioning Adani on X",
  "Brief me on Environment & Resource Conflicts",
];

export default function ChatWidget({ onJumpToMap }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text:
        "Hi — I'm CIMAP Analyst, your onboard intelligence copilot powered by Claude. Ask me about sentiment patterns, protest clusters, risky sites or specific ventures. I synthesize live signals across X, Instagram, Facebook, LinkedIn & Reddit — and when I mention a site, click the pin to zoom the map.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [sites, setSites] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    api.get("/locations").then((r) => setSites(r.data.markers || [])).catch(() => {});
  }, []);

  // Build a regex union of site names, longest-first to avoid partial overlap
  const sitePattern = useMemo(() => {
    if (sites.length === 0) return null;
    const escaped = sites
      .map((s) => s.site)
      .sort((a, b) => b.length - a.length)
      .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    return new RegExp(`(${escaped.join("|")})`, "g");
  }, [sites]);

  const siteLookup = useMemo(() => {
    const m = {};
    sites.forEach((s) => { m[s.site] = s; });
    return m;
  }, [sites]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/chat/analyst", { message: q, session_id: sessionId });
      setMessages((m) => [...m, { role: "bot", text: res.data.reply }]);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "I'm temporarily offline.";
      setMessages((m) => [...m, { role: "bot", text: `⚠ ${detail}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const handlePin = (siteName) => {
    const s = siteLookup[siteName];
    if (!s || !onJumpToMap) return;
    onJumpToMap({
      site: s.site,
      platform: "chat",
      risk_score: s.avg_risk,
      location: { lat: s.lat, lng: s.lng, state: s.state },
      id: `chat-${siteName}`,
    });
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed left-5 bottom-5 z-[900] flex items-center gap-2 bg-[#020617] text-white px-4 py-3 rounded-full shadow-lg hover:bg-[#0f172a] transition-transform hover:-translate-y-0.5"
          data-testid="chat-fab"
        >
          <Bot className="h-4 w-4 text-[#60a5fa]" />
          <span className="text-xs font-semibold uppercase tracking-[0.22em]">Ask Analyst</span>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed left-5 bottom-5 z-[900] w-[400px] max-h-[72vh] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          <header className="px-4 py-3 bg-[#020617] text-white flex items-center gap-2">
            <div className="h-7 w-7 bg-white/10 flex items-center justify-center rounded-sm">
              <Sparkles className="h-3.5 w-3.5 text-[#60a5fa]" />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold text-sm leading-none">CIMAP Analyst</div>
              <div className="text-[9px] uppercase tracking-[0.26em] text-white/50 mt-1">Claude Haiku 4.5 · Live context</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-white/70 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 overflow-auto thin-scroll p-3 space-y-3 text-[13px]">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[90%] px-3 py-2 rounded-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#2563eb] text-white"
                    : m.error
                      ? "bg-red-50 border border-red-200 text-red-700"
                      : "bg-slate-50 border border-slate-200 text-slate-800"
                }`}>
                  {m.error && <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />}
                  {m.role === "bot"
                    ? m.text.split("\n").map((line, j) => (
                        <div key={j}>{renderBotLine(line, sitePattern, handlePin)}</div>
                      ))
                    : m.text.split("\n").map((line, j) => (
                        <div key={j}>{renderPlain(line)}</div>
                      ))
                  }
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono" data-testid="chat-typing">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> analyst is synthesising…
              </div>
            )}
          </div>

          {messages.length <= 2 && !sending && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-[11px] border border-slate-200 px-2 py-1 rounded-sm hover:bg-slate-50 text-slate-600"
                  data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 p-2 border-t border-slate-200 bg-white"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about sentiment, sites, activists…"
              className="h-9 rounded-sm border-slate-300 text-sm"
              data-testid="chat-input"
              disabled={sending}
            />
            <Button type="submit" size="icon" disabled={sending || !input.trim()} className="h-9 w-9 rounded-sm bg-[#020617] hover:bg-[#0f172a]" data-testid="chat-send">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}

function renderPlain(line) {
  const parts = line.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => (i % 2 === 1 ? <b key={i}>{p}</b> : <span key={i}>{p}</span>));
}

/**
 * Bot line renderer:
 *  • Handles **bold** markdown.
 *  • Detects any known site name and wraps it with an inline jump-to-map pin.
 */
function renderBotLine(line, sitePattern, onPin) {
  // First split bold segments, then within non-bold segments detect sites.
  const boldParts = line.split(/\*\*(.+?)\*\*/g);
  return boldParts.flatMap((chunk, bi) => {
    const isBold = bi % 2 === 1;
    if (!sitePattern) return [isBold ? <b key={`b-${bi}`}>{chunk}</b> : <span key={`s-${bi}`}>{chunk}</span>];
    const pieces = chunk.split(sitePattern);
    return pieces.map((piece, pi) => {
      if (pi % 2 === 1) {
        // This is a site match
        return (
          <span key={`p-${bi}-${pi}`} className="inline-flex items-center gap-1 bg-white border border-slate-200 px-1.5 py-0.5 rounded-sm">
            <span className={isBold ? "font-semibold" : ""}>{piece}</span>
            <button
              onClick={() => onPin(piece)}
              className="text-[#2563eb] hover:text-[#1e40af]"
              title={`Jump to ${piece} on map`}
              data-testid={`chat-pin-${piece.replace(/\s+/g, "-")}`}
            >
              <MapPin className="h-3 w-3" />
            </button>
          </span>
        );
      }
      return isBold
        ? <b key={`p-${bi}-${pi}`}>{piece}</b>
        : <span key={`p-${bi}-${pi}`}>{piece}</span>;
    });
  });
}
